import { type FC, type RefObject, useEffect } from "react";
import { Button, message } from "antd";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDashboardPdfExport } from "src/hooks/useDashboardPdfExport";
import "./DashboardExportButton.scss";

export interface DashboardExportButtonProps {
  dashboardName: string;
  dateRange: { startDate: string; endDate: string };
  containerRef: RefObject<HTMLElement>;
  disabled?: boolean;
  onBeforeCapture?: () => Promise<void>;
  onAfterCapture?: () => void;
}

const DashboardExportButton: FC<DashboardExportButtonProps> = ({
  dashboardName,
  dateRange,
  containerRef,
  disabled = false,
  onBeforeCapture,
  onAfterCapture,
}) => {
  const { t } = useTranslation();
  const { status, errorMessage, exportPdf } = useDashboardPdfExport();

  useEffect(() => {
    if (errorMessage) {
      message.error(errorMessage);
    }
  }, [errorMessage]);

  const handleClick = () => {
    void exportPdf({
      containerEl: containerRef.current,
      dashboardName,
      dateRange,
      onBeforeCapture,
      onAfterCapture,
    });
  };

  return (
    <Button
      className="dashboard-export-button"
      data-pdf-ignore
      icon={<Download size={16} />}
      loading={status === "generating"}
      disabled={disabled || status === "generating"}
      onClick={handleClick}
    >
      {status === "generating"
        ? t("common.exportPdf.generating", { defaultValue: "Generating PDF…" })
        : t("common.exportPdf.label", { defaultValue: "Export" })}
    </Button>
  );
};

export default DashboardExportButton;
